import { useLegacyCard } from '@/src/common/hooks/useCardInformation'
import { formatDateToShortString } from '@/src/common/utils/date'
import { formatCurrency } from '@/src/common/utils/general'
import type { BalanceComponentType } from '@/src/experiences/card-payments/screens/MakePayment/Balance'
import { useEvent } from 'tamagui'

export function LegacyBalance({ BalanceComponent }: { BalanceComponent: BalanceComponentType }) {
  const cardInformationQuery = useLegacyCard()
  const refetch = useEvent(() => void cardInformationQuery.refetch())
  const cardInformation = cardInformationQuery.data?.getCreditCardInformation.node

  if (!cardInformation) return <BalanceComponent refetchCardAccountSummary={refetch} />

  return (
    <BalanceComponent
      refetchCardAccountSummary={refetch}
      currentBalance={{
        value: cardInformation.financialAccount.outstandingBalancePayable,
        formatted: formatCurrency(
          cardInformation.financialAccount.outstandingBalancePayable.toString(),
        ),
      }}
      statementBalance={{
        value: cardInformation.financialAccount.currentAmountDue,
        formatted: formatCurrency(cardInformation.financialAccount.currentAmountDue.toString()),
      }}
      minimumPayment={
        cardInformation.financialAccount.statementMinimumPayment
          ? {
              value: cardInformation.financialAccount.statementMinimumPayment,
              formatted: formatCurrency(cardInformation.financialAccount.statementMinimumPayment),
            }
          : { formatted: 'N/A' }
      }
      statementDueOn={(() => {
        if (!cardInformation.financialAccount.statementPaymentDueOn) return { formatted: 'N/A' }
        const value = new Date(cardInformation.financialAccount.statementPaymentDueOn)
        return { value, formatted: formatDateToShortString(value) }
      })()}
      paymentCardNextClosingDate={
        cardInformation.financialAccount.paymentCardNextClosingDate
          ? formatDateToShortString(
              new Date(cardInformation.financialAccount.paymentCardNextClosingDate),
              { timeZone: 'UTC' },
            )
          : 'N/A'
      }
      availableCredit={
        cardInformation.financialAccount.availableCredit
          ? {
              value: cardInformation.financialAccount.availableCredit,
              formatted: formatCurrency(cardInformation.financialAccount.availableCredit),
            }
          : undefined
      }
      paymentCardCreditLine={
        cardInformation.financialAccount.paymentCardCreditLine
          ? {
              value: cardInformation.financialAccount.paymentCardCreditLine,
              formatted: formatCurrency(cardInformation.financialAccount.paymentCardCreditLine),
            }
          : undefined
      }
      purchaseAPR={cardInformation.financialAccount.paymentCardAPR ?? undefined}
    />
  )
}
